import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentCopy } from '@mui/icons-material';
import { LinksService } from '../../api/services/LinksService';
import type { CreateLinkInput } from '../../api/models/CreateLinkInput';

const CopyToClipboardButton = ({
  text,
  IconComponent = ContentCopy,
  color,
  width,
  orderPageId,
  disabled,
  alwaysShorten,
}: {
  text: string;
  IconComponent?: any;
  color?: string;
  width?: string;
  orderPageId?: string;
  disabled?: boolean;
  alwaysShorten?: boolean;
}) => {
  const { t } = useTranslation();
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false);
  const [isSmallScreenCopyOpen, setIsSmallScreenCopyOpen] = useState(false);
  const [shortLink, setShortLink] = useState('');

  const handleClick = async () => {
    if (disabled) {
      return;
    }
    if (
      !alwaysShorten &&
      !text.startsWith(window.location.origin) &&
      !text.startsWith(import.meta.env.VITE_REACT_APP_APP_DOMAIN)
    ) {
      let updatedText = text;
      if (text.startsWith('972')) {
        updatedText = text.replace('972', '0');
      }
      await navigator.clipboard.writeText(updatedText);
      setIsSuccessSnackbarOpen(true);
      return;
    }
    try {
      const requestBody: CreateLinkInput = { long_link: text, order_page_id: orderPageId };
      const response = await LinksService.createShortLink(requestBody);
      const { short_code } = response;
      const shortLink = `${import.meta.env.VITE_REACT_APP_LINKS_HOST}/${short_code}`;
      setShortLink(shortLink);

      try {
        await navigator.clipboard.writeText(shortLink);
        setIsSuccessSnackbarOpen(true);
      } catch (error) {
        console.error('Failed to copy short link:', error);
        setIsSmallScreenCopyOpen(true);
      }
    } catch (error) {
      console.error('Failed to create short link:', error);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shortLink);
      setIsSmallScreenCopyOpen(false);
      setIsSuccessSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to copy short link in fallback:', error);
    }
  };

  return (
    <>
      <IconComponent
        onClick={handleClick}
        cursor={disabled ? 'default' : 'pointer'}
        color={disabled ? 'disabled' : undefined}
        style={{ color: disabled ? undefined : color, width }}
      />
      <Snackbar
        open={isSuccessSnackbarOpen}
        onClose={() => setIsSuccessSnackbarOpen(false)}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setIsSuccessSnackbarOpen(false)} severity="success">
          {t('copiedToClipboard')}
        </Alert>
      </Snackbar>
      <Dialog open={isSmallScreenCopyOpen} onClose={() => setIsSmallScreenCopyOpen(false)}>
        <DialogTitle>{t('copyToClipboard.copyFailed')}</DialogTitle>
        <DialogContent>
          <DialogContentText dir="ltr" sx={{ overflowWrap: 'anywhere' }}>
            {shortLink}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSmallScreenCopyOpen(false)} variant="outlined" color="inherit">
            {t('close')}
          </Button>
          <Button onClick={handleCopy} color="primary" variant="contained">
            <Grid container gap={1}>
              <ContentCopy style={{ width: 16 }} />
              {t('copyToClipboard.copy')}
            </Grid>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CopyToClipboardButton;
